import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
// Import Local Utils
import { deliveryRange } from '../../utils/businessDays';
// Import Hooks
import {
  CARD_REPLACEMENT_STATES,
  DEFAULT_DELIVERY_WINDOW,
} from '../../hooks/useCardReplacement';
// Import Component Styles
import { PageBanner, ActivateLink } from './styles';
import supportContactValues from './SupportContact';

const bold = chunks => <b>{chunks}</b>;
// "Activate it here": opens the activate dialog in place.
const activateLink = onActivate => chunks => (
  <ActivateLink type="button" onClick={onActivate}>
    {chunks}
  </ActivateLink>
);

// Month and day, as the mockup shows them: the window is never more than a few weeks out.
export const DATE_FORMAT = { month: 'short', day: 'numeric' };

// French abbreviated months end in a full stop ("19 nov."), which doubles up before the
// sentence's own; the full month reads naturally there.
export const dateFormat = locale =>
  locale && locale.startsWith('fr') ? { month: 'long', day: 'numeric' } : DATE_FORMAT;

const endOfDay = date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

// Shown on the dashboard from the order until the new card is activated, when the
// account's reissue_requested_at goes back to null. Within the delivery window it
// says the card is on the way; once the last expected day has passed it says the card
// should have arrived and sends the cardholder to support. Both offer activation. The old
// card's sentence is left out when the server could not tell which card was cancelled.
// "Activate it here" opens the dashboard's activate dialog (onActivate).
const CardReplacementBanner = ({ state, requestedAt, previousLastFour, onActivate }) => {
  const intl = useIntl();

  if (state !== CARD_REPLACEMENT_STATES.IN_PROGRESS || !requestedAt) return null;

  const { from, to } = deliveryRange(new Date(requestedAt), DEFAULT_DELIVERY_WINDOW);
  // Date.now rather than new Date(), so tests can fix today.
  const overdue = Date.now() > endOfDay(to).getTime();

  // One wrapper, so the banner's flex layout keeps the space after the bold lead-in.
  return (
    <PageBanner $tone="success">
      <div>
        <FormattedMessage
          id={overdue ? 'replace-card-overdue-alert' : 'replace-card-on-the-way-alert'}
          values={{
            b: bold,
            link: activateLink(onActivate),
            ...supportContactValues(),
            from: intl.formatDate(from, dateFormat(intl.locale)),
            to: intl.formatDate(to, dateFormat(intl.locale)),
            lastFour: previousLastFour || 'none',
          }}
        />
      </div>
    </PageBanner>
  );
};

CardReplacementBanner.propTypes = {
  state: PropTypes.string.isRequired,
  requestedAt: PropTypes.string,
  previousLastFour: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
};

CardReplacementBanner.defaultProps = {
  requestedAt: null,
  previousLastFour: null,
};

export default CardReplacementBanner;
